import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import {
  useTracks,
  useParticipants,
  useLocalParticipant,
  useChat,
} from "@livekit/components-react";
import { Track } from "livekit-client";

import MeetingRoomFeatures from "./MeetingRoomFeatures";

import Icon from "./ui/Icon";
import LiveKitVideo from "./ui/LiveKitVideo";
import JungleBackground from "./ui/JungleBackground";
import NatureTable from "./ui/NatureTable";
import Seat from "./ui/Seat";
import PresentersRow from "./ui/PresentersRow";
import ActiveSpeakerBanner from "./ui/ActiveSpeakerBanner";

import AgendaHint from "./modals/AgendaHint";
import AgendaModal from "./modals/AgendaModal";
import ParticipantsModal from "./modals/ParticipantsModal";
import ChatModal from "./modals/ChatModal";
import NetworkingInfoModal from "./modals/NetworkingInfoModal";
import ProfileModal from "./modals/ProfileModal";
import NetworkingDetailsModal from "@/components/NetworkingDetailsModal";
import FullscreenParticipantModal from "./modals/FullscreenParticipantModal";
import TableSwitchModal from "./modals/TableSwitchModal";

import ParticipantsPanel from "./panels/ParticipantsPanel";
import MeetChatPanel from "./panels/MeetChatPanel";
import TableSwitch from "./panels/TableSwitch";
import InfoPanel from "./panels/InfoPanel";

import { userIdFromIdentity } from "./utils/identity";
import { getRoleImageFromMetaOrApi } from "./utils/participantMeta";

import { useTableRosterPolling } from "./hooks/useTableRosterPolling";
import { useSessionTablesPolling } from "./hooks/useSessionTablesPolling";
import { useSeatPositions } from "./hooks/useSeatPositions";
import { useMergedParticipants } from "./hooks/useMergedParticipants";
import { useJoinLeaveToasts } from "./hooks/useJoinLeaveToasts";
import { useUnreadChat } from "./hooks/useUnreadChat";
import MiniFloatingControls from "./ui/MiniFloatingControls";
import { usePictureInPicture } from "./hooks/usePictureInPicture";

import { useEvent } from "@/context/EventContext";
import {
  getSessionTables,
  getEventSessions,
  getSessionsWithMeetingStatus,
} from "@/Services/EventMeetingServices";

import { useSocket } from "@/helpers/useSocket";
import { socketService } from "@/Services/socketService";

import { useBreakoutRoom } from "./hooks/useBreakoutRoom";
import CreateBreakoutModal from "@/components/UserMeet/CreateBreakoutModal";
import BreakoutInviteModal from "@/components/UserMeet/BreakoutInviteModal";
import BreakoutOverlay from "@/components/UserMeet/BreakoutOverlay";

/* ----------------------------- constants ----------------------------- */

const DEVICE_ERRORS = {
  BOTH_DENIED: {
    message:
      "Camera and microphone access denied. Click the [i] icon in your browser's address bar to allow access.",
    icon: "ℹ️",
    severity: "error",
  },
  MIC_DENIED: {
    message:
      "Microphone access denied. Click the [i] icon in your browser's address bar to allow access.",
    icon: "ℹ️",
    severity: "error",
  },
  CAMERA_DENIED: {
    message:
      "Camera access denied. Click the [i] icon in your browser's address bar to allow access.",
    icon: "ℹ️",
    severity: "error",
  },
  MIC_IN_USE: {
    message:
      "Microphone is being used by another application. Please close other apps and try again.",
    icon: "⚠️",
    severity: "warning",
  },
  CAMERA_IN_USE: {
    message:
      "Camera is being used by another application. Please close other apps and try again.",
    icon: "⚠️",
    severity: "warning",
  },
  MIC_NOT_FOUND: {
    message: "No microphone found. Please connect a microphone and try again.",
    icon: "🎤",
    severity: "warning",
  },
  CAMERA_NOT_FOUND: {
    message: "No camera found. Please connect a camera and try again.",
    icon: "📷",
    severity: "warning",
  },
  BOTH_NOT_FOUND: {
    message:
      "No camera or microphone detected. You'll join in listen-only mode.",
    icon: "🚫",
    severity: "error",
  },
  MIC_HARDWARE_MISSING: {
    message: "No microphone detected. You can use video but not audio.",
    icon: "⚠️",
    severity: "warning",
  },
  CAMERA_HARDWARE_MISSING: {
    message: "No camera detected. You can use audio but not video.",
    icon: "⚠️",
    severity: "warning",
  },
};

/* ----------------------------- internal UI (kept here for now) ----------------------------- */

function ScreenShareStage({ screenShareTrack, apiUserMap, onStopPresenting }) {
  if (!screenShareTrack) return null;

  const { participant } = screenShareTrack;
  const { name } = getRoleImageFromMetaOrApi(participant, apiUserMap);
  const isLocalPresenter = participant?.isLocal;

  return (
    <div className="jt-screenShareStage">
      <div className="jt-screenShareContainer">
        <LiveKitVideo
          trackRef={screenShareTrack}
          muted
          objectFit="contain"
          className="jt-screenShareVideo"
        />

        <div className="jt-screenShareInfo">
          <div className="jt-screenShareBadge">
            <span className="jt-screenShareIcon">
              <Icon name="screen" />
            </span>
            <span className="jt-screenShareText">
              {isLocalPresenter
                ? "You are presenting"
                : `${name} is presenting`}
            </span>
          </div>

          {isLocalPresenter && onStopPresenting && (
            <button
              className="jt-stopPresentingBtn"
              onClick={onStopPresenting}
              type="button"
            >
              <span className="jt-stopPresentingIcon">
                <Icon name="screenShareOff" />
              </span>
              <span className="jt-stopPresentingText">Stop presenting</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- main stage (inside LiveKit context) ----------------------------- */

export default function JungleTableStage({
  tableName,
  tableIcon,
  connected,
  apiParticipants,
  apiUserMap,
  permissions,
  onLeave,
  sessionId,
  eventId,
  currentTableId,
  onRequestTableChange,
  authToken,
  deviceStatus,
  setDeviceStatus,
}) {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useParticipants();

  const { fetchEventById, eventById } = useEvent();
  const currentEvent = eventById[eventId] || null;

  const [sessions, setSessions] = useState([]);
  const [sessionsWithMeetingStatus, setSessionsWithMeetingStatus] = useState([]);

  useEffect(() => {
    if (eventId) {
      fetchEventById(eventId);
      
      getEventSessions(eventId)
        .then((res) => setSessions(res?.data ?? res ?? []))
        .catch(console.error);
        
      getSessionsWithMeetingStatus(eventId)
        .then((res) => setSessionsWithMeetingStatus(res?.data ?? res ?? []))
        .catch(console.error);
    }
  }, [eventId, fetchEventById]);

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.trim();

  // ✅ roster polling hook (authoritative user ids)
  const tableRoster = useTableRosterPolling({
    sessionId,
    tableId: currentTableId,
    pollMs: 2000,
  });

  // console.log("tableroster", tableRoster)

  // ✅ best source for "my user id"
  const myUserId = useMemo(() => {
    if (tableRoster?.currentUser?._id) return tableRoster.currentUser._id;

    const fromIdentity = userIdFromIdentity(localParticipant?.identity || "");
    if (fromIdentity) return fromIdentity;

    const meFromApi = (apiParticipants || []).find((p) => p?.isCurrentUser);
    return meFromApi?._id || null;
  }, [
    tableRoster?.currentUser?._id,
    localParticipant?.identity,
    apiParticipants,
  ]);

  const nameByUserId = useMemo(() => {
    const m = {};
    const roster = tableRoster?.participants || [];
    for (const u of roster) {
      if (u?._id && u?.name) m[String(u._id)] = u.name;
    }
    return m;
  }, [tableRoster?.participants]);

  const myName =
    tableRoster?.currentUser?.name || localStorage.getItem("fullName") || "";
    
  const myNetworkingDetails = useMemo(() => {
    const me =
      tableRoster?.currentUser ||
      (tableRoster?.participants || []).find(
        (p) => String(p._id) === String(myUserId),
      );
    return {
      networkingRole: me?.networkingRole || "",
      networkingIntent: me?.networkingIntent || "",
    };
  }, [tableRoster, myUserId]);

  const [showParticipants, setShowParticipants] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [netInfoKey, setNetInfoKey] = useState(null);
  const [fsParticipant, setFsParticipant] = useState(false);
  const [showTableSwitch, setShowTableSwitch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const [switching, setSwitching] = useState(false);

  // ── Breakout state ──────────────────────────────────────────
const [showCreateBreakout, setShowCreateBreakout]   = useState(false);
const [createBreakoutLoading, setCreateBreakoutLoading] = useState(false);
const [inviteLoading, setInviteLoading]             = useState(false);
const [inviteResponseLoading, setInviteResponseLoading] = useState(false);
const [breakoutOpen, setBreakoutOpen]               = useState(false);

// Track participant statuses (for "In Private Meet" badge)
const [participantStatuses, setParticipantStatuses] = useState({});

  const [showAgenda, setShowAgenda] = useState(false);
  const [showAgendaHint, setShowAgendaHint] = useState(false);
  const hasOpenedAgendaRef = useRef(false);

  // ✅ session tables polling hook
  // const fetchTables = useCallback(() => {
  //   if (!sessionId) return Promise.resolve(null);
  //   return getSessionTables(sessionId);
  // }, [sessionId]);

  // const { tables } = useSessionTablesPolling({
  //   sessionId,
  //   fetchTables,
  //   pollMs: 5000,
  // });
  const { tables } = useSessionTablesPolling({
  sessionId,
  pollMs: 5000,
});

  const currentTable = useMemo(() => {
    if (!currentTableId) return null;
    return (tables || []).find(
      (t) => String(t?._id || t?.id) === String(currentTableId),
    );
  }, [tables, currentTableId]);

  const resolvedTableIcon =
    currentTable?.sponcer?.image ||
    currentTable?.tableIcon ||
    tableIcon ||
    null;

  const resolvedTableName = currentTable?.tableName || tableName || "Meeting";

  const currentSession = useMemo(() => {
    const allSessions = [...sessions, ...sessionsWithMeetingStatus];
    return allSessions.find((s) => String(s?._id || s?.id) === String(sessionId));
  }, [sessions, sessionsWithMeetingStatus, sessionId]);

  const resolvedSessionName = currentSession?.name || currentSession?.sessionName || "";
  const agendaHtml = currentTable?.agenda || "";

  // after connected: show hint once after 5 seconds
  useEffect(() => {
    if (!connected) return;

    const t = setTimeout(() => {
      if (!hasOpenedAgendaRef.current) setShowAgendaHint(true);
    }, 5000);

    return () => clearTimeout(t);
  }, [connected]);

  // auto-hide hint after 2.5s
  useEffect(() => {
    if (!showAgendaHint) return;
    const t = setTimeout(() => setShowAgendaHint(false), 2500);
    return () => clearTimeout(t);
  }, [showAgendaHint]);

  // if user never opened agenda, re-show hint every 30–45 seconds
  useEffect(() => {
    if (!connected) return;
    if (hasOpenedAgendaRef.current) return;
    if (showAgendaHint) return;

    const delay = 30000 + Math.floor(Math.random() * 15000); // 30-45s
    const t = setTimeout(() => {
      if (!hasOpenedAgendaRef.current) setShowAgendaHint(true);
    }, delay);

    return () => clearTimeout(t);
  }, [connected, showAgendaHint]);

  const openAgenda = useCallback(() => {
    hasOpenedAgendaRef.current = true;
    setShowAgendaHint(false);
    setShowAgenda(true);
  }, []);

  const openSponsorInfo = useCallback(() => {
    const partner = currentTable?.sponsorDetails || currentTable?.sponcer;

    if (!partner?._id) {
      toast.error("Sponsor info not available for this table");
      return;
    }

    const storageKey = `corpPartner:${partner._id}:${Date.now()}`;

    try {
      localStorage.setItem(storageKey, JSON.stringify(partner));
    } catch (e) {
      console.error("Failed to write sponsor to localStorage:", e);
      toast.error("Unable to open sponsor info");
      return;
    }

    const url = `/corporate-information?key=${encodeURIComponent(storageKey)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [currentTable]);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const {
    screenShareTrack,
    allParticipants,
    liveOnlyParticipants,
    liveParticipantListForUI,
    participantListForModal,
    presenters,
    activeSpeaker,
    seats,
    overflowCount,
    nameByIdentity,
  } = useMergedParticipants({
    localParticipant,
    remoteParticipants,
    tracks,
    apiUserMap,
    apiParticipants,
    tableRoster,
    getRoleImageFromMetaOrApi,
  });

  const localCameraTrack = useMemo(() => {
    return (tracks || []).find(
      (t) =>
        t.source === Track.Source.Camera &&
        t.participant?.isLocal &&
        t.publication != null,
    );
  }, [tracks]);

  // ── Breakout room hook ──────────────────────────────────────
const breakout = useBreakoutRoom({
  sessionId,
  myUserId,
  onBreakoutStart: () => setBreakoutOpen(true),
  onBreakoutEnd:   () => setBreakoutOpen(false),
});

  // ✅ join/leave toasts hook
  useJoinLeaveToasts({
    connected,
    liveOnlyParticipants,
    resolvedTableName,
    currentTableId,
  });

  const onlineParticipantsCount = liveOnlyParticipants.length;

  const netInfoParticipant = useMemo(() => {
    if (!netInfoKey) return null;

    return (
      allParticipants.find(
        (p) =>
          (p.userId && String(p.userId) === String(netInfoKey)) ||
          String(p.id) === String(netInfoKey) ||
          (p.identity && String(p.identity) === String(netInfoKey)),
      ) || null
    );
  }, [allParticipants, netInfoKey]);

  useEffect(() => {
    if (netInfoKey && !netInfoParticipant) {
      setNetInfoKey(null);
    }
  }, [netInfoKey, netInfoParticipant]);

  // ✅ chat state does NOT reset on modal close
  const { chatMessages, send } = useChat();

  const sendChatMessage = useCallback(
    async (text) => {
      const msg = String(text || "").trim();
      if (!msg) return;
      await send(msg);
    },
    [send],
  );

  const liveSessionsCount = tables?.length ?? 0;

  const { seatPositions } = useSeatPositions();

  const toggleMic = useCallback(async () => {
    if (!localParticipant || !permissions.canPublishAudio) return;

    const nextState = !localParticipant.isMicrophoneEnabled;

    if (nextState) {
      try {
        await localParticipant.setMicrophoneEnabled(true);
      } catch (err) {
        console.error("Failed to enable microphone:", err);

        // Re-show banner (same behavior)
        setDeviceStatus((prev) => ({ ...prev, warningDismissed: false }));

        let errorConfig = null;

        if (err.name === "NotAllowedError")
          errorConfig = DEVICE_ERRORS.MIC_DENIED;
        else if (err.name === "NotReadableError")
          errorConfig = DEVICE_ERRORS.MIC_IN_USE;
        else if (err.name === "NotFoundError")
          errorConfig = DEVICE_ERRORS.MIC_NOT_FOUND;

        if (errorConfig) {
          toast.error(errorConfig.message, {
            duration: 5000,
            icon: errorConfig.icon,
          });
        } else {
          toast.error("Failed to enable microphone. Please try again.");
        }
      }
    } else {
      await localParticipant.setMicrophoneEnabled(false);
    }
  }, [localParticipant, permissions.canPublishAudio, setDeviceStatus]);

  const toggleCam = useCallback(async () => {
    if (!localParticipant || !permissions.canPublishVideo) return;

    const nextState = !localParticipant.isCameraEnabled;

    if (nextState) {
      try {
        await localParticipant.setCameraEnabled(true);
      } catch (err) {
        console.error("Failed to enable camera:", err);

        // Re-show banner (same behavior)
        setDeviceStatus((prev) => ({ ...prev, warningDismissed: false }));

        let errorConfig = null;

        if (err.name === "NotAllowedError")
          errorConfig = DEVICE_ERRORS.CAMERA_DENIED;
        else if (err.name === "NotReadableError")
          errorConfig = DEVICE_ERRORS.CAMERA_IN_USE;
        else if (err.name === "NotFoundError")
          errorConfig = DEVICE_ERRORS.CAMERA_NOT_FOUND;

        if (errorConfig) {
          toast.error(errorConfig.message, {
            duration: 5000,
            icon: errorConfig.icon,
          });
        } else {
          toast.error("Failed to enable camera. Please try again.");
        }
      }
    } else {
      await localParticipant.setCameraEnabled(false);
    }
  }, [localParticipant, permissions.canPublishVideo, setDeviceStatus]);

  const toggleScreenShare = useCallback(async () => {
    if (!localParticipant || !permissions.canPublishScreen) return;
    const next = !localParticipant.isScreenShareEnabled;

    try {
      await localParticipant.setScreenShareEnabled(next);
    } catch (err) {
      // User cancelled the screen picker dialog — not a real error, ignore silently
      if (
        err?.name === "NotAllowedError" ||
        err?.message?.includes("Permission denied") ||
        err?.message?.includes("canceled") ||
        err?.message?.includes("cancelled")
      ) {
        return;
      }

      // Any other unexpected error — show toast
      console.error("Screen share error:", err);
      toast.error("Failed to share screen. Please try again.");
    }
  }, [localParticipant, permissions.canPublishScreen]);

  const micOn = localParticipant?.isMicrophoneEnabled ?? false;
  const camOn = localParticipant?.isCameraEnabled ?? false;
  const isSharing = localParticipant?.isScreenShareEnabled ?? false;

  const handleSwitchTable = async (newTable) => {
    if (!newTable) return;
    const newTableId = newTable._id || newTable.id;

    if (String(newTableId) === String(currentTableId)) {
      toast.success("Already in this table");
      setShowTableSwitch(false);
      return;
    }

    setSwitching(true);
    try {
      setShowTableSwitch(false);
      await onRequestTableChange?.(newTable);
    } finally {
      setSwitching(false);
    }
  };

  const [showTab, setShowTab] = useState("tables");
  const [profileParticipant, setProfileParticipant] = useState(null);

  const { unreadChat, setUnreadChat } = useUnreadChat({
    chatCount: chatMessages.length,
    showTab,
  });

  const [networkingModalOpen, setNetworkingModalOpen] = useState(false);
  const [networkingDetails, setNetworkingDetails] = useState({
    networkingRole: "",
    networkingIntent: "",
  });

  // ✅ Auto-populate from roster polling whenever data arrives/changes
  useEffect(() => {
    if (
      myNetworkingDetails.networkingRole ||
      myNetworkingDetails.networkingIntent
    ) {
      setNetworkingDetails({
        networkingRole: myNetworkingDetails.networkingRole,
        networkingIntent: myNetworkingDetails.networkingIntent,
      });
    }
  }, [
    myNetworkingDetails.networkingRole,
    myNetworkingDetails.networkingIntent,
  ]);

  const [isTabHidden, setIsTabHidden] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabHidden(document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // ── Listen for participant-status-changed (seat badge updates) ──
useEffect(() => {
  if (!sessionId) return;

  const { on } = socketService;

  const handleStatusChanged = (data) => {
    // data: { userId, sessionId, participantStatus }
    if (data?.sessionId && String(data.sessionId) !== String(sessionId)) return;
    if (!data?.userId) return;

    setParticipantStatuses((prev) => ({
      ...prev,
      [String(data.userId)]: data.participantStatus,
    }));
  };

  socketService.on("participant-status-changed", handleStatusChanged);

  return () => {
    socketService.off("participant-status-changed", handleStatusChanged);
  };
}, [sessionId]);

  usePictureInPicture({
    enabled: isTabHidden && connected,
    videoTrackRef: localCameraTrack,
    myName,
    micOn,
    camOn,
    isSharing,
    onToggleMic: toggleMic,
    onToggleCam: toggleCam,
    onToggleScreenShare: toggleScreenShare,
    onLeave,
    participantCount: onlineParticipantsCount,
  });

  return (
    <MeetingRoomFeatures
      sessionId={sessionId}
      eventId={eventId}
      tableId={currentTableId}
      authToken={authToken}
      myUserId={myUserId}
      nameByUserId={nameByUserId}
      myName={myName}
    >
      {({
        sendGlobalEmoji,
        sendPersonalLike,
        openNotes,
        EmojiQuickBar,
        updateNetworkingStatus,
        networkingStatusLoading,
      }) => (
        <div className="jt-root">
          <JungleBackground />

          <ActiveSpeakerBanner speaker={activeSpeaker} />

          <div className="jt-header">
            <div className="jt-headerLeft">
              <span className={`jt-statusDot ${connected ? "on" : "warn"}`} />
              <div className="jt-headerTitle" title={resolvedTableName}>
                {resolvedTableName}
              </div>
              {connected && (
                <div className="jt-livePill">
                  <i />
                  LIVE
                </div>
              )}
              <span className="jt-event-name">{currentEvent?.eventName}</span>
              {/* <button
                type="button"
                className="jt-agendaInfoBtn"
                onClick={openAgenda}
                title="Open agenda"
              >
                <span className="jt-infoIcon">i</span>
              </button> */}
            </div>

            <div className="jt-headerRight">
              <EmojiQuickBar onSend={sendGlobalEmoji} />
            </div>
          </div>

          {!screenShareTrack && (
            <PresentersRow
              presenters={presenters}
              onPress={(p) => setFsParticipant(p)}
            />
          )}

          {screenShareTrack && (
            <ScreenShareStage
              screenShareTrack={screenShareTrack}
              apiUserMap={apiUserMap}
              onStopPresenting={async () => {
                if (localParticipant && permissions.canPublishScreen) {
                  await localParticipant.setScreenShareEnabled(false);
                }
              }}
            />
          )}

          <FullscreenParticipantModal
            open={!!fsParticipant}
            participant={fsParticipant}
            onClose={() => setFsParticipant(null)}
          />

          {/* <AgendaHint
            open={showAgendaHint}
            onClose={() => setShowAgendaHint(false)}
            onOpen={openAgenda}
          /> */}

          <AgendaModal
            open={showAgenda}
            onClose={() => setShowAgenda(false)}
            title={`${resolvedTableName} — Agenda`}
            agendaHtml={agendaHtml}
          />

          <ParticipantsModal
            open={showParticipants}
            onClose={() => setShowParticipants(false)}
            list={participantListForModal}
          />

          <ChatModal
            open={showChat}
            onClose={() => setShowChat(false)}
            canPublishData={permissions.canPublishData}
            nameByIdentity={nameByIdentity}
            chatMessages={chatMessages}
            onSend={sendChatMessage}
          />

          <NetworkingInfoModal
            open={!!netInfoKey && !!netInfoParticipant}
            participant={netInfoParticipant}
            onClose={() => setNetInfoKey(null)}
          />

          <ProfileModal
            open={!!profileParticipant}
            participant={profileParticipant}
            onClose={() => setProfileParticipant(null)}
            onEditNetworking={(role, intent) => {
              setProfileParticipant(null);
              setNetworkingDetails({
                // use passed role/intent, fallback to roster data
                networkingRole:
                  role || myNetworkingDetails.networkingRole || "",
                networkingIntent:
                  intent || myNetworkingDetails.networkingIntent || "",
              });
              setNetworkingModalOpen(true);
            }}
          />

          <TableSwitchModal
            open={showTableSwitch}
            onClose={() => setShowTableSwitch(false)}
            tables={tables}
            currentTableId={currentTableId}
            sessionId={sessionId}
            onSwitchTable={handleSwitchTable}
            switching={switching}
          />

          <NetworkingDetailsModal
            className="networkingmodal"
            open={networkingModalOpen}
            initial={networkingDetails}
            tableName={resolvedTableName}
            onCancel={() => setNetworkingModalOpen(false)}
            onSubmit={async (details) => {
              // ✅ Optimistic local update so modal re-opens with latest values
              setNetworkingDetails({
                networkingRole: details.networkingRole,
                networkingIntent: details.networkingIntent,
              });
              const result = await updateNetworkingStatus(details);
              if (result) {
                setNetworkingModalOpen(false);
              }
            }}
          />

          {/* ── CREATE BREAKOUT MODAL ────────────────────────────── */}
          <CreateBreakoutModal
            open={showCreateBreakout}
            onClose={() => setShowCreateBreakout(false)}
            loading={createBreakoutLoading}
            onCreate={async (timeLimitMinutes) => {
              setCreateBreakoutLoading(true);
              try {
                await breakout.createBreakout(timeLimitMinutes);
                setShowCreateBreakout(false);
              } finally {
                setCreateBreakoutLoading(false);
              }
            }}
          />

          {/* ── BREAKOUT INVITE MODAL (global overlay) ───────────── */}
          <BreakoutInviteModal
            open={!!breakout.pendingInvite}
            invite={breakout.pendingInvite}
            loading={inviteResponseLoading}
            onAccept={async () => {
              if (!breakout.pendingInvite?.breakoutId) return;
              setInviteResponseLoading(true);
              try {
                await breakout.respondToInvite(
                  breakout.pendingInvite.breakoutId,
                  true,
                );
                // ✅ hook already clears pendingInvite and calls onBreakoutStart
              } finally {
                setInviteResponseLoading(false);
              }
            }}
            onDecline={async () => {
              if (!breakout.pendingInvite?.breakoutId) return;
              try {
                await breakout.respondToInvite(
                  breakout.pendingInvite.breakoutId,
                  false,
                );
                // ✅ hook handles clearPendingInvite
              } catch {
                breakout.clearPendingInvite();
              }
            }}
            onDecline={async () => {
              if (!breakout.pendingInvite?.breakoutId) return;
              try {
                await breakout.respondToInvite(
                  breakout.pendingInvite.breakoutId,
                  false,
                );
              } finally {
                breakout.clearPendingInvite();
              }
            }}
          />

          {/* ── BREAKOUT OVERLAY (full screen private room) ──────── */}
          <BreakoutOverlay
            open={breakoutOpen && breakout.isActive}
            livekitToken={breakout.livekitToken ?? null}
            livekitServerUrl={breakout.livekitServerUrl ?? null}
            isOwner={breakout.isOwner}
            participants={breakout.participants}
            expiresAt={breakout.expiresAt}
            timeLimitMinutes={breakout.timeLimitMinutes}
            ownerDisconnected={breakout.ownerDisconnected}
            gracePeriodSeconds={breakout.gracePeriodSeconds}
            mainMeetingParticipants={liveParticipantListForUI}
            myUserId={myUserId}
            inviteLoading={inviteLoading}
            onLeave={async () => {
              // ✅ If owner clicks Leave, warn them it ends for everyone
              if (breakout.isOwner) {
                const confirmed = window.confirm(
                  "As the host, leaving will end the private meeting for everyone. Continue?",
                );
                if (!confirmed) return;
              }
              await breakout.leaveBreakout();
              setBreakoutOpen(false);
            }}
            onEnd={async () => {
              await breakout.endBreakout();
              setBreakoutOpen(false);
            }}
            onInvite={async (ids) => {
              setInviteLoading(true);
              try {
                await breakout.inviteParticipants(ids);
              } finally {
                setInviteLoading(false);
              }
            }}
          />

          <div className="jt-stage-wrp">
            <div
              className={`jt-stage ${screenShareTrack ? "jt-stage-hidden" : ""}`}
            >
              <div className="jt-tableCenter">
                <NatureTable
                  tableName={resolvedTableName}
                  tableIcon={resolvedTableIcon}
                  onPressSponsor={openSponsorInfo}
                  agendaHtml={agendaHtml}
                />
              </div>

              {seats.map((p, i) => {
                // Merge live participantStatus from socket into participant object
                const enrichedParticipant = p
                  ? {
                      ...p,
                      participantStatus:
                        participantStatuses[String(p.userId)] ??
                        p.participantStatus ??
                        "active",
                    }
                  : null;

                return (
                  <Seat
                    key={`seat-${i}`}
                    seatIndex={i}
                    pos={seatPositions[i]}
                    participant={enrichedParticipant}
                    onAvatarPress={(participant) => {
                      if (!participant) return;
                      setFsParticipant(participant);
                    }}
                    onNamePress={(participant) => {
                      if (!participant) return;
                      setProfileParticipant(participant);
                    }}
                    onLike={(toUserId) => {
                      sendPersonalLike(toUserId);
                    }}
                    onShowNetworking={(participant) => {
                      const key =
                        participant?.userId ||
                        participant?.identity ||
                        participant?.id;
                      setNetInfoKey(key || null);
                    }}
                  />
                );
              })}

              {overflowCount > 0 && (
                <div className="jt-overflowBadge" title="More participants">
                  <Icon name="people" />
                  <span>+{overflowCount} more</span>
                </div>
              )}
            </div>

            <div className="jt-sessions-tab">
              <button
                className="jt-mobileMenuBtn"
                onClick={() => setMobileMenuOpen(true)}
              >
                <i className="fa-solid fa-bars"></i>
              </button>

              {/* ================= MOBILE DRAWER ================= */}
              <div
                className={`jt-mobileDrawerOverlay ${
                  mobileMenuOpen ? "open" : ""
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <div
                  className={`jt-mobileDrawer ${mobileMenuOpen ? "open" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* HEADER */}
                  <div className="jt-mobileDrawerHeader">
                    <div className="jt-mobileDrawerTitle">Meeting Menu</div>

                    <button
                      className="jt-mobileDrawerClose"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobilePanelOpen(true);
                      }}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>

                  {/* MENU ITEMS */}
                  <button
                    onClick={() => {
                      setShowTab("tables");
                      setMobileMenuOpen(false);
                      setMobilePanelOpen(true);
                    }}
                    className={`jt-mobileDrawerItem ${
                      showTab === "tables" ? "active formob-meet" : ""
                    }`}
                  >
                    <span className="jt-mobileDrawerIcon">
                      <Icon name="grid" />
                    </span>

                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">Sessions</span>

                      <span className="jt-mobileDrawerSub">
                        {liveSessionsCount} Live
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setShowTab("participants");
                      setMobileMenuOpen(false);
                      setMobilePanelOpen(true);
                    }}
                    className={`jt-mobileDrawerItem ${
                      showTab === "participants" ? "active formob-meet" : ""
                    }`}
                  >
                    <span className="jt-mobileDrawerIcon">
                      <Icon name="people" />
                    </span>

                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">
                        Participants
                      </span>

                      <span className="jt-mobileDrawerSub">
                        {onlineParticipantsCount} Online
                      </span>
                    </div>

                    <span className="jt-mobileDrawerBadge">
                      {onlineParticipantsCount}
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setShowTab("chat");
                      setUnreadChat(0);
                      setMobileMenuOpen(false);
                      setMobilePanelOpen(true);
                    }}
                    className={`jt-mobileDrawerItem ${
                      showTab === "chat" ? "active " : ""
                    }`}
                  >
                    <span className="jt-mobileDrawerIcon">
                      <Icon name="chat" />
                    </span>

                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">Chat</span>

                      <span className="jt-mobileDrawerSub">
                        {chatMessages.length} Messages
                      </span>
                    </div>

                    {unreadChat > 0 && (
                      <span className="jt-mobileDrawerBadge jt-mobileDrawerBadgeChat">
                        {unreadChat}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      setNetworkingModalOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    className="jt-mobileDrawerItem"
                  >
                    <span className="jt-mobileDrawerIcon">
                      <i className="fa-solid fa-wifi"></i>
                    </span>
                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">
                        Networking Status
                      </span>
                      <span className="jt-mobileDrawerSub">
                        Update role &amp; intent
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setShowTab("info");

                      setMobileMenuOpen(false);
                      setMobilePanelOpen(true);
                    }}
                    className={`jt-mobileDrawerItem ${
                      showTab === "info" ? "active " : ""
                    }`}
                  >
                    <span className="jt-mobileDrawerIcon">
                      <i className="fa-solid fa-info"></i>
                    </span>

                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">Info</span>
                    </div>
                  </button>
                </div>
              </div>

              <div className="jt-topBtns">
                <button
                  onClick={() => setShowTab("tables")}
                  className={`jt-topBtn ${showTab === "tables" ? "active" : ""}`}
                >
                  <span className="jt-topBtnIcon">
                    <Icon name="grid" />
                  </span>

                  <div className="jt-topBtnContent">
                    <span className="jt-topBtnLabel">Sessions</span>
                    <span className="jt-topBtnSub">
                      {liveSessionsCount} Live
                    </span>
                  </div>
                </button>

                <button
                  onClick={() => setShowTab("participants")}
                  className={`jt-topBtn ${
                    showTab === "participants" ? "active" : ""
                  }`}
                >
                  <span className="jt-topBtnIcon">
                    <Icon name="people" />
                  </span>

                  <div className="jt-topBtnContent">
                    <span className="jt-topBtnLabel">Participants</span>
                    <span className="jt-topBtnSub">
                      {onlineParticipantsCount} Online
                    </span>
                  </div>

                  <span className="jt-topBtnBadge">
                    {onlineParticipantsCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setShowTab("chat");
                    setUnreadChat(0);
                  }}
                  className={`jt-topBtn ${showTab === "chat" ? "active" : ""}`}
                >
                  <span className="jt-topBtnIcon">
                    <Icon name="chat" />
                  </span>

                  <div className="jt-topBtnContent">
                    <span className="jt-topBtnLabel">Chat</span>
                    <span className="jt-topBtnSub">
                      {chatMessages.length} messages
                    </span>
                  </div>

                  {unreadChat > 0 && (
                    <span className="jt-topBtnBadge jt-topBtnBadgeChat">
                      {unreadChat}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowTab("info");
                  }}
                  className={`jt-topBtn ${showTab === "info" ? "active" : ""}`}
                >
                  <span className="jt-topBtnIcon">
                    {/* <Icon name="chat" /> */}
                    <i className="fa-solid fa-info"></i>
                  </span>

                  <div className="jt-topBtnContent">
                    <span className="jt-topBtnLabel">Info</span>
                    {/* <span className="jt-topBtnSub">
                      {chatMessages.length} messages
                    </span> */}
                  </div>
                </button>
              </div>

              {mobilePanelOpen && (
                <div className="formob-meet">
                  {showTab === "participants" && (
                    <ParticipantsPanel
                      list={liveParticipantListForUI}
                      onProfileClick={(p) => setProfileParticipant(p)}
                    />
                  )}

                  {showTab === "tables" && (
                    <TableSwitch
                      tables={tables}
                      currentTableId={currentTableId}
                      sessionId={sessionId}
                      onSwitchTable={handleSwitchTable}
                      switching={switching}
                    />
                  )}

                  {showTab === "chat" && (
                    <MeetChatPanel
                      canPublishData={permissions.canPublishData}
                      userNameMap={nameByIdentity}
                      chatMessages={chatMessages}
                      onMessageSend={sendChatMessage}
                    />
                  )}
                  {showTab === "info" && (
                    <InfoPanel
                      eventId={eventId}
                      tableName={resolvedTableName}
                      sessionName={resolvedSessionName}
                    />
                  )}
                </div>
              )}

              <div className="for-desk-meet">
                {showTab === "participants" && (
                  <ParticipantsPanel
                    list={liveParticipantListForUI}
                    onProfileClick={(p) => setProfileParticipant(p)}
                  />
                )}

                {showTab === "tables" && (
                  <TableSwitch
                    tables={tables}
                    currentTableId={currentTableId}
                    sessionId={sessionId}
                    onSwitchTable={handleSwitchTable}
                    switching={switching}
                  />
                )}

                {showTab === "chat" && (
                  <MeetChatPanel
                    canPublishData={permissions.canPublishData}
                    userNameMap={nameByIdentity}
                    chatMessages={chatMessages}
                    onMessageSend={sendChatMessage}
                  />
                )}
                {showTab === "info" && (
                  <InfoPanel
                    eventId={eventId}
                    tableName={resolvedTableName}
                    sessionName={resolvedSessionName}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="jt-controls">
            <div className="jt-controlsBar">
              {permissions.canPublishAudio && (
                <button
                  className={`jt-ctlBtn ${micOn ? "active" : "danger"}`}
                  type="button"
                  onClick={toggleMic}
                  title={micOn ? "Mute" : "Unmute"}
                >
                  <div className={`jt-ctlIcon ${micOn ? "active" : "danger"}`}>
                    {micOn ? <Icon name="mic" /> : <Icon name="micOff" />}
                  </div>
                  <div className="jt-ctlLabel">{micOn ? "Mute" : "Unmute"}</div>
                </button>
              )}

              {permissions.canPublishVideo && (
                <button
                  className={`jt-ctlBtn ${camOn ? "active" : "danger"}`}
                  type="button"
                  onClick={toggleCam}
                  title={camOn ? "Camera off" : "Camera on"}
                >
                  <div className={`jt-ctlIcon ${camOn ? "active" : "danger"}`}>
                    {camOn ? <Icon name="cam" /> : <Icon name="camOff" />}
                  </div>
                  <div className="jt-ctlLabel">
                    {camOn ? "Camera" : "No Cam"}
                  </div>
                </button>
              )}

              {permissions.canPublishScreen && (
                <button
                  className={`jt-ctlBtn ${isSharing ? "broadcast" : "inactive"}`}
                  type="button"
                  onClick={toggleScreenShare}
                  title={isSharing ? "Stop sharing" : "Share screen"}
                >
                  <div
                    className={`jt-ctlIcon ${
                      isSharing ? "broadcast" : "inactive"
                    }`}
                  >
                    <Icon name="screen" />
                  </div>
                  <div className="jt-ctlLabel">
                    {isSharing ? "Stop" : "Share"}
                  </div>
                </button>
              )}

              {/* <button
                className="jt-ctlBtn"
                type="button"
                onClick={() => {
                  setShowTab("chat");
                  setUnreadChat(0);
                }}
              >
                <div
                  className="jt-ctlIcon inactive"
                  style={{ position: "relative" }}
                >
                  <Icon name="chat" />
                  {unreadChat > 0 && (
                    <span className="jt-badge">
                      {unreadChat > 99 ? "99+" : unreadChat}
                    </span>
                  )}
                </div>
                <div className="jt-ctlLabel">Chat</div>
              </button> */}

              <button className="jt-ctlBtn" type="button">
                <div onClick={openNotes} className="jt-ctlIcon">
                  <i className="fa-solid fa-note-sticky"></i>
                </div>
                <div className="jt-ctlLabel">Note</div>
              </button>

              {/* Private Meeting button */}
              {breakout.isActive ? (
                <button
                  className="jt-ctlBtn breakout"
                  type="button"
                  onClick={() => setBreakoutOpen(true)}
                  title="Return to private meeting"
                >
                  <div className="jt-ctlIcon">
                    <span style={{ fontSize: 18 }}>🔒</span>
                  </div>
                  <div className="jt-ctlLabel" style={{ color: "#FCD34D" }}>
                    Private
                  </div>
                </button>
              ) : (
                <button
                  className="jt-ctlBtn"
                  type="button"
                  onClick={() => setShowCreateBreakout(true)}
                  title="Start Private Meeting"
                >
                  <div className="jt-ctlIcon inactive">
                    <span style={{ fontSize: 18 }}>🔒</span>
                  </div>
                  <div className="jt-ctlLabel">Private</div>
                </button>
              )}

              <button
                className="jt-ctlBtn"
                type="button"
                onClick={() => setNetworkingModalOpen(true)}
                title="Networking Status"
              >
                <div className="jt-ctlIcon inactive">
                  <i className="fa-solid fa-wifi"></i>
                </div>
                <div className="jt-ctlLabel">Status</div>
              </button>

              <button
                className="jt-ctlBtn"
                type="button"
                onClick={onLeave}
                title="Leave"
              >
                <div className="jt-ctlIcon leave">
                  <Icon name="leave" />
                </div>
                <div className="jt-ctlLabel leaveTxt">Leave</div>
              </button>
            </div>
          </div>
          <MiniFloatingControls
            visible={(isTabHidden || isSharing) && connected}
            localTrackRef={localCameraTrack}
            micOn={micOn}
            camOn={camOn}
            isSharing={isSharing}
            onToggleMic={toggleMic}
            onToggleCam={toggleCam}
            onToggleScreenShare={toggleScreenShare}
            onLeave={onLeave}
            canPublishAudio={permissions.canPublishAudio}
            canPublishVideo={permissions.canPublishVideo}
            canPublishScreen={permissions.canPublishScreen}
            participantCount={onlineParticipantsCount}
            myName={myName}
          />
        </div>
      )}
    </MeetingRoomFeatures>
  );
}
